// Thermal-printer transport over the Web Serial API (Chrome/Edge).
//
// This is the "no-install" path from the hardware spec: the browser talks
// directly to a USB ESC/POS printer after a one-time permission, and the
// cash drawer opens through the printer's kick port. If Web Serial isn't
// available (other browsers, Android) or the venue uses a network printer,
// callers fall back to the browser print dialog — nothing here throws in a
// way that blocks the sale.
//
// NOTE: this needs a real printer to validate; there is no way to test the
// serial round-trip without hardware. Baud rate may need tuning per model.

import { buildReceipt, drawerKickBytes } from "./escpos";

let _port = null;

export function serialSupported() {
  return typeof navigator !== "undefined" && "serial" in navigator;
}

export function isConnected() {
  return !!_port;
}

// Must be called from a user gesture (click) — the browser shows a device
// picker. Remembers the chosen port for the rest of the session.
export async function connectPrinter(baudRate = 9600) {
  if (!serialSupported()) {
    throw new Error("This browser can't connect directly to a printer. Use Chrome, or the print helper.");
  }
  const port = await navigator.serial.requestPort();
  await port.open({ baudRate });
  _port = port;
  return true;
}

export async function disconnect() {
  try { if (_port) await _port.close(); } catch (e) { /* ignore */ }
  _port = null;
}

async function _write(bytes) {
  if (!_port) throw new Error("No printer connected.");
  const writer = _port.writable.getWriter();
  try {
    await writer.write(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes));
  } finally {
    writer.releaseLock();
  }
}

// Build the receipt from a bill object and send it. Returns true on success,
// false if we couldn't print (so the caller can fall back to browser print).
export async function printReceipt(receipt, opts = {}) {
  if (!serialSupported() || !_port) return false;
  try {
    await _write(buildReceipt(receipt, opts));
    return true;
  } catch (e) {
    return false;
  }
}

// Open the cash drawer without printing (e.g. a "no sale" button).
export async function openDrawer() {
  if (!serialSupported() || !_port) return false;
  try {
    await _write(drawerKickBytes());
    return true;
  } catch (e) {
    return false;
  }
}
